// validators/event.js

const eventValidators = {
  // Step 2 → Title
  2: ({ step1 }, t) => {
    if (!step1.name?.trim()) {
      return t("please_fill_title");
    }
    if (step1.name.trim().length > 32) {
      return t("title_too_long");
    }
    return null;
  },

  // Step 3 → Event Type
  3: ({ step1 }, t) => {
    if (!step1.events_type_id) {
      return t("please_select_event_type");
    }
    return null;
  },

  // Step 4 → Location
  4: ({ step1 }, t) => {
    if (!step1.country_id) {
      return t("please_fill_country");
    }
    if (!step1.city_id) {
      return t("please_fill_city");
    }
    if (!step1.phone?.trim()) {
      return t("please_fill_phone");
    }
    if (!step1.address?.trim()) {
      return t("please_fill_address");
    }
    return null;
  },

  // Step 6 → Description
  6: ({ step2 }, t) => {
    if (!step2.short_summary?.trim()) {
      return t("please_fill_summary");
    }
    if (step2.short_summary.trim().length < 32) {
      return t("summary_too_short");
    }
    if (step2.short_summary.trim().length > 200) {
      return t("summary_too_long");
    }
    if (!step2.detailed_description?.trim()) {
      return t("please_fill_description");
    }
    if (step2.detailed_description.trim().length < 60) {
      return t("description_too_short");
    }
    if (step2.detailed_description.trim().length > 500) {
      return t("description_too_long");
    }

    return null;
  },

  // Step 7 → Objectives/Attributes
  7: ({ step2 }, t) => {
    if (
      !step2.objectives ||
      !Array.isArray(step2.objectives) ||
      step2.objectives.length === 0
    ) {
      return t("please_select_at_least_one_objective");
    }
    return null;
  },

  // Step 9 → Details (Capacity, Event Status, Platform/Details)
  9: ({ step3 }, t) => {
    // Validate capacity
    if (!step3.capacity || step3.capacity <= 0) {
      return t("please_enter_valid_capacity");
    }

    // Validate status
    if (
      !step3.status ||
      (step3.status !== "InPerson" && step3.status !== "OnLine")
    ) {
      return t("please_select_event_status");
    }

    // Validate status-specific fields
    if (step3.status === "InPerson") {
      if (!step3.details?.trim()) {
        return t("please_fill_event_details");
      }
    }

    if (step3.status === "OnLine") {
      if (!step3.platform) {
        return t("please_select_platform");
      }
      if (!step3.access_link?.trim()) {
        return t("please_fill_access_link");
      }
      // Validate URL format
      try {
        new URL(step3.access_link);
      } catch {
        return t("invalid_access_link_format");
      }
    }

    return null;
  },

  // Step 10 → Policy (Dates, Times, Cutoff Time, Cancellation)
  10: ({ step3 }, t) => {
    // Validate dates
    if (!step3.start_date) {
      return t("please_fill_start_date");
    }

    if (!step3.end_date) {
      return t("please_fill_end_date");
    }

    // Validate date range
    const startDate = new Date(step3.start_date);
    const endDate = new Date(step3.end_date);

    if (startDate >= endDate) {
      return t("end_date_must_be_after_start_date");
    }

    // Validate times
    if (!step3.start_hour?.trim()) {
      return t("please_fill_start_time");
    }

    if (!step3.end_hour?.trim()) {
      return t("please_fill_end_time");
    }

    if (step3.start_hour >= step3.end_hour) {
      return t("end_time_must_be_after_start_time");
    }

    // Validate cutoff time
    if (
      !step3.cutoff_time_booking_value ||
      step3.cutoff_time_booking_value < 1
    ) {
      return t("please_fill_cutoff_time");
    }

    // Validate cancellation policy
    if (
      step3.cancellation_policy_id === undefined ||
      step3.cancellation_policy_id === null
    ) {
      return t("please_fill_cancellation_policy_id");
    }

    return null;
  },

  // Step 11 → Booking Confirmation
  11: ({ step3 }, t) => {
    if (!step3.booking_confirmation_mode) {
      return t("please_fill_booking_confirmation_mode");
    }

    if (
      step3.booking_confirmation_mode !== "instant" &&
      step3.booking_confirmation_mode !== "manual"
    ) {
      return t("invalid_booking_confirmation_mode");
    }

    return null;
  },

  // Step 12 → Pricing
  12: ({ step3 }, t) => {
    if (!step3.price || step3.price <= 0) {
      return t("please_enter_valid_price");
    }
    return null;
  },

  // Step 13 → Review (Final validation before submission)
  13: ({ step1, step2, step3 }, t) => {
    // Validate step1 fields
    if (!step1.name?.trim()) {
      return t("please_fill_title_before_submit");
    }

    if (!step1.events_type_id) {
      return t("please_select_event_type_before_submit");
    }

    if (!step1.country_id) {
      return t("please_fill_country_before_submit");
    }

    if (!step1.city_id) {
      return t("please_fill_city_before_submit");
    }

    // Validate step2 fields
    if (!step2.short_summary?.trim()) {
      return t("please_fill_summary_before_submit");
    }

    if (!step2.detailed_description?.trim()) {
      return t("please_fill_description_before_submit");
    }

    if (!step2.objectives || step2.objectives.length === 0) {
      return t("please_select_objectives_before_submit");
    }

    // Validate step3 fields
    if (!step3.capacity || step3.capacity <= 0) {
      return t("please_enter_capacity_before_submit");
    }

    if (!step3.start_date) {
      return t("please_fill_start_date_before_submit");
    }

    if (!step3.end_date) {
      return t("please_fill_end_date_before_submit");
    }

    if (
      step3.cancellation_policy_id === undefined ||
      step3.cancellation_policy_id === null
    ) {
      return t("please_fill_cancellation_policy_before_submit");
    }

    if (!step3.booking_confirmation_mode) {
      return t("please_fill_booking_confirmation_mode_before_submit");
    }

    if (!step3.price || step3.price <= 0) {
      return t("please_enter_price_before_submit");
    }

    return null;
  },

  // No shared validators - everything is custom
  shared: [],
};

export default eventValidators;
